import { useState } from 'react';
import { Alert, Button, Spin, Typography } from 'antd';
import { FaUserMinus } from 'react-icons/fa6';

const { Title, Text } = Typography;

function parseEmails(raw) {
  return raw
    .split(/[\n\r,;|\s]+/)
    .map(s => s.trim().toLowerCase())
    .filter(s => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s));
}

export default function BulkUnsubscribeForm() {
  const [raw, setRaw] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const emails = parseEmails(raw);

  const handleSubmit = async () => {
    if (emails.length === 0) return;
    setLoading(true);
    setResult(null);
    setError(null);
    try {
      const token = localStorage.getItem('prae.jwt');
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/admin/bulk-unsubscribe`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ emails }),
        }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Erreur ${res.status}`);
      }
      const data = await res.json();
      setResult(data);
      setRaw('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 pt-20">
      <div className="flex items-center gap-3 mb-6">
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-red-600 text-white shadow-sm">
          <FaUserMinus />
        </span>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            Désabonnement en masse
          </Title>
          <Text type="secondary">
            Collez les adresses emails à désabonner (une par ligne, ou séparées
            par virgule/point-virgule)
          </Text>
        </div>
      </div>

      <div className="mb-4">
        <textarea
          value={raw}
          onChange={e => setRaw(e.target.value)}
          placeholder={
            'creation@example.com\ncontact@autre.fr\ninfo@domaine.com'
          }
          rows={14}
          className="w-full rounded-lg border border-slate-300 p-3 text-sm font-mono resize-y focus:outline-none focus:ring-2 focus:ring-indigo-500"
          style={{ minHeight: 200 }}
        />
      </div>

      <div className="flex items-center justify-between mb-4">
        <Text type={emails.length > 0 ? 'success' : 'secondary'}>
          {emails.length > 0
            ? `${emails.length} adresse${emails.length > 1 ? 's' : ''} valide${emails.length > 1 ? 's' : ''} détectée${emails.length > 1 ? 's' : ''}`
            : 'Aucune adresse valide détectée'}
        </Text>
        <Button
          type="primary"
          danger
          icon={<FaUserMinus />}
          disabled={emails.length === 0 || loading}
          onClick={handleSubmit}
          loading={loading}
        >
          Désabonner {emails.length > 0 ? `(${emails.length})` : ''}
        </Button>
      </div>

      {loading && (
        <div className="flex justify-center py-4">
          <Spin tip="Traitement en cours…" />
        </div>
      )}

      {result && (
        <div className="mt-2">
          <div className="mb-3 text-sm font-medium text-green-700">
            ✓ {result.inserted} adresse{result.inserted > 1 ? 's' : ''} traitée
            {result.inserted > 1 ? 's' : ''}
          </div>
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="bg-slate-100 text-left">
                <th className="px-3 py-2 font-medium text-slate-600 border border-slate-200">
                  Email
                </th>
                <th className="px-3 py-2 font-medium text-slate-600 border border-slate-200">
                  Statut
                </th>
                <th className="px-3 py-2 font-medium text-slate-600 border border-slate-200">
                  Dernier broadcast reçu
                </th>
                <th className="px-3 py-2 font-medium text-slate-600 border border-slate-200">
                  Date
                </th>
              </tr>
            </thead>
            <tbody>
              {result.summary.map(row => (
                <tr key={row.email} className="hover:bg-slate-50">
                  <td className="px-3 py-1.5 border border-slate-200 font-mono">
                    {row.email}
                  </td>
                  <td className="px-3 py-1.5 border border-slate-200">
                    {row.already_unsubscribed ? (
                      <span className="text-amber-600">Déjà désabonné</span>
                    ) : (
                      <span className="text-green-600">
                        Désabonnement effectué ✅
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-1.5 border border-slate-200 text-slate-700">
                    {row.last_broadcast ? (
                      row.last_broadcast.name
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-3 py-1.5 border border-slate-200 text-slate-500">
                    {row.last_broadcast ? (
                      new Date(row.last_broadcast.sent_at).toLocaleDateString(
                        'fr-FR'
                      )
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {error && (
        <Alert type="error" showIcon message="Erreur" description={error} />
      )}
    </div>
  );
}
